import React, { useState, useEffect } from "react";
import { Alert, Button, Disclaimer, ImageSelectionCard } from "../../components"
import { useApi } from "../../api";
import { useSelector } from "react-redux";
import store, { RootState } from "../../store";
import { getSelectedCommercialProducts, getSelectedCommercialProductsWithFeatureFlag } from "../../config/product-config";
import { CommercialProduct } from "../../config/types/product-config-settings";
import { Lob, SaveQuoteRequest, SAVE_QUOTE_RESPONSE } from "../../api/types";
import { clearCache, updateQuote, updateQuoteResponse } from "../../store/quote/action";
import { GeicoPageNames, IGeicoDtm, useAnalytics } from "../../contexts/AnalyticsContext";
import { usePageLoadingContext } from "../../contexts/PageLoadingContext";
import useDevice from "../../hooks/useDevice";
import { BASE_APP_PAth, API_ERROR_MESSAGE } from "../../api/launchpad-api";
import { useNavigate } from "react-router-dom";
import { useClientConfigs } from "../../contexts/ClientConfigContext";
import { useAuthContext } from "../../contexts/AuthContextProvider";
import { useAutoRedirectContext } from "../../contexts/AutoRedirectContext";
import { handleAuthenticatedRedirect } from "../../utils/authenticated-redirects";
import { BINDING_RESTRICTIONS_RESPONSE } from "../../api/types/binding-restrictions-model";

export const ProductSelection = () => {
    const api = useApi();
    const navigate = useNavigate();
    const clientConnfig = useClientConfigs();
    const quote = useSelector((state: RootState) => state.quote.quote);
    const authContext = useAuthContext()
    const [commercialProducts, setCommercialProducts]
        = useState(getSelectedCommercialProductsWithFeatureFlag(
            clientConnfig.userType,
            quote.data.SelectedProducts,
            authContext.getUserRoles(),
            true,
            clientConnfig.userType === "Customer" ? clientConnfig.availableProductsCustomer : undefined
        ));
    const [formError, setFormError] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState({ title: "", message: "" });
    const [showBindingRestrictionAlert, setShowBindingRestrictionAlert] = useState(false);
    const [hasBindingRestriction, setHasBindingRestriction] = useState(false);
    const { showLoader, hideLoader, pageState } = usePageLoadingContext();
    const loading = pageState.sectionLoading;
    const [productError, setProductError] = useState("");
    const { geicoDtm, setGeicoDtm, sendToAnalytics } = useAnalytics();
    //const [redirectToPartner, setRedirectToPartner] = useState("");
    // const [showPopUpConsent, setShowPopUpConsent] = useState(false);
    const deviceDetail = useDevice();
    const redirectContext = useAutoRedirectContext();
    const productSelectionPageName = GeicoPageNames.Launchpad + ":Product-Selection";

    useEffect(() => {
        if (quote.data.Zip && quote.data.StateCode) {
            api.commercialAgentApi.getBindingRestrictions(quote.data.Zip)
                .then((bindingResponse) => {
                    setHasBindingRestriction(
                        bindingResponse.type === BINDING_RESTRICTIONS_RESPONSE && bindingResponse.data?.isEligible === false
                    );
                });
        }
    }, [quote.data.StateCode, quote.data.Zip]);

    useEffect(() => {
        try {
            showLoader();
            setGeicoDtm({ ...geicoDtm, viewName: productSelectionPageName });
            setCommercialProducts(
                getSelectedCommercialProductsWithFeatureFlag(
                    clientConnfig.userType,
                    quote.data.SelectedProducts,
                    authContext.getUserRoles(),
                    true,
                    clientConnfig.userType === "Customer" ? clientConnfig.availableProductsCustomer : undefined
                ));
            setTimeout(() => {
                if (redirectContext.isAutoRedirect && redirectContext.product && redirectContext.launchpadId) {
                    onNextClick();
                }
                else if (redirectContext.isAutoRedirect || getSelectedProducts().length > 0) {
                    onNextClick();
                }
                else {
                    hideLoader();
                }
            }, 200);
        }
        catch (err) {
            console.error(err);
            hideLoader();
        }
    }, []);

    useEffect(() => {
        let selectedProduct = commercialProducts?.find(item => item.selected);
        // verifyRedirectPartner(selectedProduct);
        if (productError !== "" || selectedProduct == undefined) {
            setFormError(true);
        }
        else if (selectedProduct.lobCode === "CAU" && clientConnfig.cauDisabledState?.includes(quote.data.StateCode)) {
            setFormError(true);
        }
        else {
            setFormError(false);
        }

        var selectedProducts = getSelectedProducts();
        store.dispatch(updateQuote({ ...quote.data, SelectedProducts: selectedProducts }));

        // Adobe Geico DTM update zip state         
        setGeicoDtm({
            ...geicoDtm,
            viewName: productSelectionPageName,
            product: selectedProducts && selectedProducts.length > 0 ? commercialProducts.find(p => p.lobCode === selectedProducts[0].LobCode)?.adobeDtmName : "",
            crossSellType: "",
            crossSellOffer: "",
            serviceMilestone: []
        });

    }, [productError, commercialProducts]);

    function clearAlertMessage() {
        setAlertMessage({
            title: "",
            message: ""
        });
    }

    const setProduct = (product: CommercialProduct) => {
        if (product.lobCode === "CAU" && clientConnfig.cauDisabledState?.includes(quote.data.StateCode)) {
            setShowAlert(true);
            const message = clientConnfig.userType === "Agent"
                ? "The zip code entered is not in a state that offers Commercial Auto. Please continue quoting through a partner."
                : "To start a Commercial Auto/Trucking quote, please call us at (866) 509-9444. Our agents are ready to help M-F 8am - 9pm ET or Sat 9am - 5:30pm. We look forward to taking care of your insurance needs.";

            setAlertMessage({
                title: "Thank you for your request.",
                message: message
            });

            const updatedProducts = commercialProducts.map(item => ({
                ...item,
                selected: item.lobCode === "CAU"
            }));
            setCommercialProducts(updatedProducts);
            return;
        } else {
            if (showAlert) {
                setShowAlert(false);
            }
            const isSelectingCau = product.lobCode === "CAU" && !product.selected;
            if (!isSelectingCau) {
                setShowBindingRestrictionAlert(false);
            }
            let updatedcommercialProducts: CommercialProduct[] = [];
            // Allow Single Item or Multiple       
            updatedcommercialProducts = commercialProducts.map(item =>
            (item.id === product.id ?
                { ...item, selected: !product.selected, disabled: false }
                : { ...item, selected: false, disabled: false }))
            setCommercialProducts(updatedcommercialProducts);
            setProductError("");
            setFormError(false);

            if (isSelectingCau) {
                setShowBindingRestrictionAlert(hasBindingRestriction);
            }
        }
    }

    const getSelectedProducts = (): Lob[] => {
        let selectedProducts: Lob[] = [];
        if (commercialProducts) {
            commercialProducts.forEach(item => {
                if (item.selected) {
                    let selectedlob = new Lob();
                    selectedlob.LobCode = item.lobCode;
                    selectedlob.LobName = item.lobName;
                    selectedlob.SspCode = item.sspCode
                    selectedProducts.push(selectedlob);
                }
            });
        }

        return selectedProducts;
    }

    const submitForm = async (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        e.preventDefault();
        await onNextClick()
    }

    const onNextClick = async () => {
        if (!formError) {
            showLoader();
            const selectedProducts = getSelectedProducts();
            if (quote.data.LaunchpadId && !redirectContext.launchpadId) {
                if (quote.data.LaunchpadId !== null && quote.data.LaunchpadId !== "") {
                    quote.data.LaunchpadId = "";
                }
            }


            const saveQuoteRequest: SaveQuoteRequest = {
                ...quote.data,
                SelectedProducts: selectedProducts,
                BusinessKeyword: null,
            };
            //store.dispatch(updateQuote(saveQuoteRequest));
            // Adobe Geico DTM Track call
            // Init adobe Dtm
            const adobeDtm: IGeicoDtm =
            {
                ...geicoDtm,
                viewName: productSelectionPageName,
                zip: saveQuoteRequest.Zip,
                state: saveQuoteRequest.StateCode,
                crossSellType: "",
                product: commercialProducts.flatMap(p => p.selected === true ? p.adobeDtmName : []).join(','),
                serviceMilestone: geicoDtm.serviceMilestone,
            };

            setGeicoDtm(adobeDtm);
            sendToAnalytics(false); // Send
            // End of Adobe DTM

            let isComplete = true;

            if (commercialProducts.find(p => p.selected && p.askBusinessKeyword)) {
                isComplete = false;
            }

            if (redirectContext.isAutoRedirect) {
                isComplete = false;
            }

            if (!clientConnfig.isMultiplePartnerRecommendationEnabled && clientConnfig.userType === "Agent") {
                isComplete = true;
            }

            await api.commercialAgentApi.saveQuote(saveQuoteRequest, isComplete)
                .then((quoteResponse) => {
                    if (quoteResponse && quoteResponse.type !== SAVE_QUOTE_RESPONSE) {
                        window.scrollTo(0, 0);
                        setShowAlert(true);
                        store.dispatch(updateQuote(
                            { ...quote.data, LaunchpadId: "", SelectedProducts: [] }
                        ))
                        setAlertMessage({
                            title: "Thank you for your request, Please refresh and try again.",
                            message: API_ERROR_MESSAGE
                        });
                        hideLoader();
                        return;
                    }

                    if (quote.data) {
                        store.dispatch(updateQuoteResponse(quoteResponse));
                        selectedProducts.forEach((lob) => {
                            if (lob.LobCode === "CAU" || lob.LobCode === "CYBER") {
                                setGeicoDtm({ ...geicoDtm, launchpadId: quoteResponse.data.launchpadId });
                            }
                        });
                    }
                    if (!isComplete) {
                        navigate(BASE_APP_PAth + "/business-keyword");
                    }
                    else {
                        // Handle redirect based on whether authentication is required
                        if (quoteResponse.data.redirectUrl && quoteResponse.data.redirectUrl.length > 0) {
                            store.dispatch(clearCache());

                            if (quoteResponse.data.isAuthenticated) {
                                // Handle authenticated redirects (e.g., Liberty Mutual Bonds)
                                const productCode = selectedProducts[0]?.LobCode;
                                
                                handleAuthenticatedRedirect(
                                    productCode,
                                    quoteResponse.data.redirectUrl,
                                    clientConnfig
                                ).then(() => {
                                }).catch((error) => {
                                    console.error("Error with authenticated redirect:", error);
                                    setShowAlert(true);
                                    setAlertMessage({
                                        title: "Thank you for your request.",
                                        message: "There was an error connecting to the carrier. Please try again."
                                    });
                                    hideLoader();
                                });
                            } else {
                                window.open(quoteResponse.data.redirectUrl, "_self");
                            }
                        }
                    }
                    setFormError(false);

                }, (error) => {
                    setShowAlert(true);
                    setAlertMessage({
                        title: "Thank you for your request.",
                        message: API_ERROR_MESSAGE
                    });
                    hideLoader();
                });
        }
    };

    // const showConsent = () => {
    //     let headerText = "Thank you for visiting GEICO"
    //     let disclaimerText = "You're leaving geico.com and being taken to our partner's website " + redirectToPartner + ", to get your quote started.";
    //     return <PopupModal modalId="consent-model"
    //         onClosed={() => setShowPopUpConsent(false)}
    //         showModal={showPopUpConsent}
    //         modalHeadline={headerText}
    //         autoShow={false}
    //         isBackNav={false}
    //         modalType={"dialogueBox"}
    //         overlayShouldCloseModal={true}
    //         showCloseButton={true} >
    //         {disclaimerText}
    //         <div className="modal--call-to-action-bar">
    //             <button type="button" onClick={submitForm}
    //                 className={deviceDetail.isMobile ? "btn--primary nav-button-next xs:w-full sm:w-full " : "btn--primary nav-button-next pull-right  w-2/5"} >
    //                 <span>Continue</span>
    //             </button>
    //         </div>
    //     </PopupModal>
    // }

    return (
        loading ? null :
            <div className="container" style={{ padding: "1rem", width: "95%" }}>
                <div className="row">
                    <div className="col-sm-12 col-md-12">
                        {(showAlert && (alertMessage.title || alertMessage.message) &&
                            <Alert
                                title={alertMessage.title}
                                content={alertMessage.message}
                                closeAlert={() => clearAlertMessage()}
                            />
                        )}
                        {showBindingRestrictionAlert && (
                            <Alert
                                title="Coverage Restrictions in Effect"
                                content="Physical damage coverage is currently not available in your area. Liability coverage is still available. Physical damage coverage may be added after restrictions end."
                                alertImportance="medium"
                            />
                        )}
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12 col-md-12" >
                        <h2 className="page-header-center " style={{ padding: "0rem", marginBottom: "1rem" }}>
                            Select a business insurance type.
                        </h2>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12 col-md-12">
                        <div id="productSelection" style={{ padding: "1rem", marginBottom: ".1rem" }} className="image-selection-wrapper">
                            <fieldset className="form-field" role="radiogroup" >
                                <div className="image-selections " style={{ justifyItems: "flex-start" }}>
                                    {commercialProducts && (
                                        commercialProducts.map((product, index) => (
                                            <ImageSelectionCard
                                                key={product.id}
                                                id={product.id}
                                                name={product.lobCode}
                                                disabled={product.disabled}
                                                label={product.label}
                                                show={true}
                                                image={process.env.PUBLIC_URL + "/assets/" + product.image}
                                                description={product.description}
                                                items={product.items}
                                                selected={product.selected}
                                                error={productError !== ""}
                                                onClick={() => setProduct(product)}
                                            />
                                        ))
                                    )}
                                </div>
                                {productError && productError !== "" && <span className="form-field-wrapper error-message form-message"> {productError}</span>}

                            </fieldset>
                        </div>
                    </div>
                </div>
                {/* {redirectToPartner !== "" ? showConsent() : null} */}
                <div className="row" >
                    <div className="col-sm-12 col-md-6">
                        {/*Enable in case we have to shod back on this screen*/}
                        <Button style={{ display: "none" }}
                            className="btn--secondary nav-button-back w-1/5 xs:w-full sm:w-full"
                        >Back</Button>
                    </div>
                    <div className="col-sm-12 col-md-6 pull-left" style={deviceDetail.isMobile ? { border: "0px", position: "fixed", backgroundColor: "#fafafa", bottom: "0px", left: "0px", width: "100%", padding: ".5rem", zIndex: "5", alignItems: "center" } : {}} >
                        <Button
                            disabled={(loading || formError)}
                            className="btn--primary nav-button-next mt-10  w-3/5 xs:w-full sm:w-full"
                            onClick={(e) => submitForm(e)}
                        >Next</Button>
                    </div>
                </div>
                <div className="row" >
                    <div className="col-sm-12 col-md-12" style={{ marginBottom: "40px" }}>
                        <Disclaimer headerLabel="Legal Notices" />
                    </div>
                    <div className="col-sm-12 col-md-12">
                    </div>
                </div>
            </div>
    );
};

